import { Icon, ProfilePicture } from "@sellar-io/components";
import {
  ACCOUNT_SETTINGS,
  ACCOUNT_NOTIFICATION_SETTINGS,
  BUSINESS_SETTINGS,
  SUPPLIERS,
  ORDERS,
} from "../../../constants/routes";
import {
  NavigationOverlayBody,
  NavigationOverlayGroup,
  NavigationOverlayHeader,
} from "./NavigationOverlayLayout";
import { NavigationOverlayRow } from "./NavigationOverlayRow";
import { useNavigationOverlayPresentation } from "./NavigationOverlayContext.shared";

export const AccountNavigationContent = ({
  name,
  email,
  imageUrl,
  businessName,
  companyName,
  onSwitchBusiness,
  onClose,
  onHelp,
  onSignOut,
  signingOut,
  signOutFailed,
  loading,
  profileFailed,
  onRetryProfile,
}: {
  name: string;
  email?: string;
  imageUrl?: string | null;
  businessName?: string;
  companyName?: string | null;
  onSwitchBusiness: () => void;
  onClose: () => void;
  onHelp: () => void;
  onSignOut: () => void;
  signingOut: boolean;
  signOutFailed: boolean;
  loading: boolean;
  profileFailed: boolean;
  onRetryProfile: () => void;
}) => {
  const isPanel = useNavigationOverlayPresentation() === "panel";
  const chevron = (
    <Icon icon="CHEVRON_RIGHT" size="16px" className="text-zinc-500" />
  );
  const avatar = (
    <ProfilePicture
      name={name}
      logoUrl={imageUrl}
      className={
        isPanel
          ? "size-8 max-h-8 min-h-8 max-w-8 min-w-8"
          : "size-11 max-h-11 min-h-11 max-w-11 min-w-11"
      }
    />
  );
  return (
    <>
      {isPanel && <NavigationOverlayHeader title="Account" onClose={onClose} />}
      <NavigationOverlayBody>
        {isPanel && businessName && (
          <NavigationOverlayGroup>
            <NavigationOverlayRow
              variant="identity"
              autoFocus
              onClick={onSwitchBusiness}
              description={companyName}
              leading={
                <span className="flex size-10 items-center justify-center rounded-full bg-purple-100 text-purple-700">
                  <Icon icon="STOREFRONT_OUTLINE" size="20px" />
                </span>
              }
              trailing={
                <Icon
                  icon="SWAP_VERTICAL"
                  size="20px"
                  className="text-purple-700"
                />
              }
              titleClassName="text-base leading-6 font-semibold"
              descriptionClassName="text-sm leading-5 font-normal text-zinc-500"
              className="[&>span]:break-words"
            >
              {businessName}
            </NavigationOverlayRow>
          </NavigationOverlayGroup>
        )}
        {!isPanel && (
          <div className="flex items-center gap-3 rounded-lg bg-zinc-100 p-3">
            {avatar}
            <div className="min-w-0 flex-1 break-words">
              <p className="text-base leading-6 font-semibold">{name}</p>
              {email && (
                <p className="text-sm leading-5 font-normal text-zinc-500">
                  {email}
                </p>
              )}
            </div>
          </div>
        )}
        {loading && (
          <p role="status" className="text-xs text-zinc-500">
            Loading account details…
          </p>
        )}
        {profileFailed && (
          <p role="status" className="text-xs text-zinc-500">
            Unable to refresh account details.{" "}
            <button
              type="button"
              onClick={onRetryProfile}
              className="text-purple-700 underline"
            >
              Try again
            </button>
          </p>
        )}
        {isPanel && businessName && (
          <NavigationOverlayGroup title="Business">
            <NavigationOverlayRow
              to={SUPPLIERS}
              leading={<Icon icon="ACCOUNT_MULTIPLE_OUTLINE" size="16px" />}
              trailing={chevron}
              className="text-zinc-700"
            >
              My Suppliers
            </NavigationOverlayRow>
            <NavigationOverlayRow
              to={ORDERS}
              leading={<Icon icon="CART_OUTLINE" size="16px" />}
              trailing={chevron}
              className="text-zinc-700"
            >
              Order history
            </NavigationOverlayRow>
            <NavigationOverlayRow
              to={BUSINESS_SETTINGS}
              leading={<Icon icon="COG_OUTLINE" size="16px" />}
              trailing={chevron}
              className="text-zinc-700"
            >
              Business settings
            </NavigationOverlayRow>
          </NavigationOverlayGroup>
        )}
        <NavigationOverlayGroup title={isPanel ? "Account" : undefined}>
          <NavigationOverlayRow
            variant={isPanel ? "identity" : "option"}
            to={ACCOUNT_SETTINGS}
            leading={isPanel ? avatar : <Icon icon="COG_OUTLINE" size="16px" />}
            description={isPanel ? email : undefined}
            descriptionClassName="text-sm leading-5 font-normal text-zinc-500"
            trailing={chevron}
            className="text-zinc-700"
          >
            {isPanel ? "Settings" : "Account settings"}
          </NavigationOverlayRow>
          <NavigationOverlayRow
            to={ACCOUNT_NOTIFICATION_SETTINGS}
            leading={<Icon icon="BELL_OUTLINE" size="16px" />}
            trailing={chevron}
            className="text-zinc-700"
          >
            Notification preferences
          </NavigationOverlayRow>
        </NavigationOverlayGroup>
        {isPanel && (
          <NavigationOverlayGroup>
            <NavigationOverlayRow
              onClick={onHelp}
              className="text-zinc-700"
              leading={<Icon icon="HELP_CIRCLE_OUTLINE" size="16px" />}
            >
              Help
            </NavigationOverlayRow>
          </NavigationOverlayGroup>
        )}
        <div className={isPanel ? "mt-2" : "border-t border-zinc-200 pt-2"}>
          <NavigationOverlayGroup>
            <NavigationOverlayRow
              onClick={onSignOut}
              pending={signingOut}
              leading={<Icon icon="EXIT_TO_APP" size="16px" />}
              trailing={chevron}
              className="text-red-700"
            >
              {signingOut ? "Signing out…" : "Sign out"}
            </NavigationOverlayRow>
          </NavigationOverlayGroup>
          {signOutFailed && (
            <p role="alert" className="px-3 py-2 text-sm text-red-700">
              Unable to sign out. Please try again.
            </p>
          )}
        </div>
      </NavigationOverlayBody>
    </>
  );
};
